import { Add } from '@mui/icons-material';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    getLocalCategoriesList,
    isCategoryUncategorized,
} from 'entities/Category';
import {
    getGlobalCategoriesList,
    getGlobalCategoriesListIsLoading,
    getLocalCategoriesListIsLoading,
} from 'entities/Category/model/selectors/categorySelectors';
import { getSelectedPropertyCatalogId } from 'entities/Property/model/selectors/PropertySelectors';
import { getUserCanEditCatalogCategories } from 'entities/User/model/selectors/userPermissionsSeletors';
import { CategoryForm } from 'features/CategoryForm';
import { CategoryFormMode } from 'features/CategoryForm/module/types/categoryFormsTypes';
import { DynamicTooltip } from 'shared/ui/Tooltip';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

import { PropertyCatalogCategory } from './PropertyCatalogCategory';

interface PropertyCatalogCategoryListProps {
    isEditing: boolean;
    isGlobal?: boolean;
    moveTo: PropertyCatalogItemsMoveTo;
    fillHeight?: boolean;
}

const skeletonsNum = 9;

export const PropertyCatalogCategoryList = ({
    isEditing,
    isGlobal,
    moveTo,
    fillHeight = false,
}: PropertyCatalogCategoryListProps) => {
    const { t } = useTranslation();
    const localCategories = useSelector(getLocalCategoriesList);
    const globalCategories = useSelector(getGlobalCategoriesList);
    const isLoadingLocal = useSelector(getLocalCategoriesListIsLoading);
    const isLoadingGlobal = useSelector(getGlobalCategoriesListIsLoading);
    const canEditCatalogCategories = useSelector(
        getUserCanEditCatalogCategories,
    );
    const catalogId = useSelector(getSelectedPropertyCatalogId);

    const [categoriesEditing, setCategoriesEditing] = useState(false);
    const isMoveToCatalog = moveTo === PropertyCatalogItemsMoveTo.CATALOG;

    const startEdit = () => {
        setCategoriesEditing(true);
    };

    const stopEdit = () => {
        setCategoriesEditing(false);
    };

    const isGlobalCatalog = !!isGlobal;

    const categoriesAreEditable =
        canEditCatalogCategories && !isGlobalCatalog && isMoveToCatalog;

    const rawCategories = isGlobalCatalog ? globalCategories : localCategories;
    const isLoading = isGlobalCatalog ? isLoadingGlobal : isLoadingLocal;

    const categories = rawCategories
        ? Object.values(rawCategories).sort((a, b) => {
              if (isCategoryUncategorized(a.title)) return -1;
              if (isCategoryUncategorized(b.title)) return 1;
              return 0;
          })
        : null;

    const heightOfListForEditMode = isEditing
        ? 'calc(100vh - 332px)'
        : 'calc(100vh - 280px)';

    const addCategoryLabel = t('Add new category');

    return (
        <Box
            sx={{
                bgcolor: 'grey.100',
                flexBasis: '50%',
                flex: 1,
                display: 'flex',
                maxHeight: fillHeight ? undefined : heightOfListForEditMode,
                height: fillHeight ? '100%' : undefined,
                minHeight: '0',
                flexDirection: 'column',
            }}
        >
            <Box
                data-testid="categories-list"
                sx={{
                    overflowY: 'auto',
                    flex: 1,
                    minHeight: 0,
                    padding: '16px 16px 8px',
                    boxSizing: 'border-box',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'grey.100',
                        position: 'sticky',
                        top: '-16px',
                        zIndex: 2,
                        margin: '-16px -16px 8px',
                        padding: '16px 16px 8px',
                    }}
                >
                    <Typography typography="poppins.subtitle2">
                        {isGlobal
                            ? t('Marketplace Categories')
                            : t('Property Categories')}
                    </Typography>
                    {!isEditing && categoriesAreEditable ? (
                        <DynamicTooltip content={addCategoryLabel}>
                            <IconButton
                                data-testid="add-category-button"
                                data-testclass="add-category-button"
                                aria-label={addCategoryLabel}
                                onClick={startEdit}
                                disabled={categoriesEditing}
                                size="small"
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    padding: 0,
                                    color: '#3F6EB7',
                                    borderRadius: '4px',
                                    ':hover': {
                                        color: '#3F6EB7',
                                        bgcolor: 'rgba(63, 110, 183, 0.1)',
                                    },
                                    '&.Mui-disabled': {
                                        color: 'rgba(63, 110, 183, 0.4)',
                                    },
                                }}
                            >
                                <Add sx={{ width: '24px', height: '24px' }} />
                            </IconButton>
                        </DynamicTooltip>
                    ) : null}
                </Box>

                {categoriesEditing && (
                    <CategoryForm
                        mode={CategoryFormMode.CREATE}
                        catalogId={catalogId}
                        onClose={stopEdit}
                        formTitle={t('New Category')}
                    />
                )}

                {categories
                    ? categories.map((category) => (
                          <Box
                              data-testclass={`category`}
                              key={category.id}
                              sx={{
                                  marginBottom: '8px',
                                  display: isLoading ? 'none' : undefined,
                              }}
                          >
                              <PropertyCatalogCategory
                                  category={category}
                                  editable={categoriesAreEditable}
                              />
                          </Box>
                      ))
                    : null}

                {Array.from({ length: skeletonsNum }).map((_, index) => (
                    <Skeleton
                        sx={{
                            marginBottom: '8px',
                            display: !isLoading ? 'none' : undefined,
                        }}
                        key={index}
                        variant="rounded"
                        height="36px"
                    />
                ))}
            </Box>
        </Box>
    );
};
